import { addDoc, collection, onSnapshot, orderBy, query, serverTimestamp, setDoc, doc } from "firebase/firestore";
import { db } from "../firebase";
import { notifyCoachForOrg } from "./notifyCoach";
import { recordRuntimeDiagnostic } from "./runtimeDiagnostics";

export const COACH_THREAD_SYNC_EVENT = "nia-coach-thread-sync";

function canUseStorage() {
  return typeof window !== "undefined" && window.localStorage;
}

function dispatchCoachThreadSync(detail = {}) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(COACH_THREAD_SYNC_EVENT, { detail }));
  } catch {
    // ignore dispatch failures
  }
}

export function getCoachThreadSessionId(session = {}) {
  return String(session?.id || session?.createdAt || "").trim();
}

export function hasCoachThreadHistory(session = {}) {
  return Boolean(
    session?.hasCoachThread ||
    Number(session?.lastCoachMessageAt || 0) ||
    Number(session?.lastMemberMessageAt || 0)
  );
}

export function getCoachThreadReadKey(userId, sessionId) {
  const normalizedUserId = String(userId || "").trim();
  const normalizedSessionId = String(sessionId || "").trim();
  if (!normalizedUserId || !normalizedSessionId) return "";
  return `nia_coach_read_${normalizedUserId}_${normalizedSessionId}`;
}

export function getCoachThreadLastRead(userId, sessionId) {
  if (!canUseStorage()) return 0;
  const readKey = getCoachThreadReadKey(userId, sessionId);
  if (!readKey) return 0;
  try {
    return Number(window.localStorage.getItem(readKey) || 0);
  } catch {
    return 0;
  }
}

export function markCoachThreadRead(userId, sessionId, viewedAt = Date.now()) {
  if (!canUseStorage()) return;
  const readKey = getCoachThreadReadKey(userId, sessionId);
  const nextReadAt = Number(viewedAt || Date.now()) || Date.now();
  if (!readKey) return;
  try {
    const current = Number(window.localStorage.getItem(readKey) || 0);
    if (nextReadAt > current) {
      window.localStorage.setItem(readKey, String(nextReadAt));
      dispatchCoachThreadSync({ type: "read", userId, sessionId, viewedAt: nextReadAt });
    }
  } catch {
    // allow silent fallback
  }
}

export function getCoachThreadIncomingAt(session = {}, viewerRole = "member") {
  const normalizedRole = String(viewerRole || "member").toLowerCase();
  if (normalizedRole === "member") return Number(session?.lastCoachMessageAt || 0);
  return Number(session?.lastMemberMessageAt || 0);
}

export function getSessionCoachThreadMessages(messages = [], sessionId = "") {
  const normalizedSessionId = String(sessionId || "").trim();
  if (!normalizedSessionId) return [];
  return (Array.isArray(messages) ? messages : [])
    .filter((message) => String(message?.sessionId || "").trim() === normalizedSessionId);
}

export function buildCoachThreadSessionList(sessions = [], user = null, viewerRole = "member") {
  const normalizedRole = String(viewerRole || user?.role || "member").toLowerCase();
  const normalizedUserId = String(user?.uid || "").trim();
  return (Array.isArray(sessions) ? sessions : [])
    .filter((session) => hasCoachThreadHistory(session))
    .map((session) => {
      const sessionId = getCoachThreadSessionId(session);
      const incomingAt = getCoachThreadIncomingAt(session, normalizedRole);
      const ownLastAt = normalizedRole === "member"
        ? Number(session?.lastMemberMessageAt || 0)
        : Number(session?.lastCoachMessageAt || 0);
      const lastRead = normalizedUserId ? getCoachThreadLastRead(normalizedUserId, sessionId) : 0;
      const lastTs = Math.max(
        Number(session?.lastCoachMessageAt || 0),
        Number(session?.lastMemberMessageAt || 0),
        Number(session?.createdAt || 0),
      );
      return {
        ...session,
        _threadId: sessionId,
        _incomingAt: incomingAt,
        _lastTs: lastTs,
        _unread: Boolean(sessionId && incomingAt > Math.max(ownLastAt, lastRead)),
      };
    })
    .sort((a, b) => (b._lastTs || 0) - (a._lastTs || 0));
}

export function getCoachThreadUnreadCount(sessions = [], user = null, viewerRole = "member") {
  return buildCoachThreadSessionList(sessions, user, viewerRole).filter((session) => session._unread).length;
}

export function listenCoachThread(user, orgId, sessionId, onUpdate, onError = null) {
  if (!user?.uid || !orgId || !sessionId || !onUpdate) return () => {};
  try {
    const base = collection(db, "orgs", orgId, "sessions", String(sessionId), "coachThread");
    const q = query(base, orderBy("createdAt", "asc"));
    const documentPath = `orgs/${orgId}/sessions/${String(sessionId)}/coachThread`;
    const attachedAt = new Date().toISOString();
    recordRuntimeDiagnostic("coach-thread.listener-attached", {
      documentPath,
      attachedAt,
      selectedSessionId: String(sessionId),
      selectedSessionIdType: typeof sessionId,
    });
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        recordRuntimeDiagnostic("coach-thread.snapshot", {
          documentPath,
          documentCount: rows.length,
          fromCache: Boolean(snap?.metadata?.fromCache),
          hasPendingWrites: Boolean(snap?.metadata?.hasPendingWrites),
        });
        dispatchCoachThreadSync({ type: "thread_update", sessionId: String(sessionId), count: rows.length });
        onUpdate(rows);
      },
      (error) => {
        recordRuntimeDiagnostic("coach-thread.listener-error", {
          documentPath,
          code: String(error?.code || ""),
          message: String(error?.message || error || ""),
        });
        if (typeof onError === "function") onError(error);
      }
    );
    return () => {
      recordRuntimeDiagnostic("coach-thread.listener-detached", {
        documentPath,
        attachedAt,
        detachedAt: new Date().toISOString(),
      });
      unsubscribe();
    };
  } catch {
    return () => {};
  }
}

export async function sendCoachThreadMessage(user, orgId, sessionId, text) {
  if (!user?.uid || !orgId || !sessionId || !text) return;
  if (user.role === "investor") return;
  const isCoachish = user.role === "coach" || user.role === "admin" || user.role === "superadmin";
  const payload = {
    text: String(text),
    senderId: user.uid,
    senderRole: user.role || "member",
    senderName: user.username || user.email || "User",
    senderEmail: user.email || "",
    createdAt: Date.now(),
    createdAtServer: serverTimestamp(),
  };
  try {
    const base = collection(db, "orgs", orgId, "sessions", String(sessionId), "coachThread");
    await addDoc(base, payload);
    const sessionRef = doc(db, "orgs", orgId, "sessions", String(sessionId));
    await setDoc(
      sessionRef,
      {
        hasCoachThread: true,
        ...(isCoachish
          ? { lastCoachMessageAt: payload.createdAt, lastCoachMessageBy: payload.senderName }
          : { lastMemberMessageAt: payload.createdAt, lastMemberMessageBy: payload.senderName }),
      },
      { merge: true }
    );
    if (isCoachish) {
      await notifyCoachForOrg(user, orgId, {
        subject: "Coach reply sent",
        message: `Coach reply on session ${sessionId}: ${payload.text}`,
      });
    }
    dispatchCoachThreadSync({
      type: "message_sent",
      sessionId: String(sessionId),
      orgId: String(orgId),
      senderRole: payload.senderRole,
      createdAt: payload.createdAt,
    });
  } catch {
    // allow silent fallback
  }
}
